/**
 * Base UI Component Class
 * Provides common functionality for all UI components
 */

export class UIComponent {
  constructor (element, options = {}) {
    this.element = element;
    this.options = { ...this.getDefaultOptions(), ...options };
    this.eventListeners = new Map();
    this.isDestroyed = false;
  }

  /**
   * Get default options for the component
   */
  getDefaultOptions () {
    return {
      className: '',
      id: '',
      attributes: {}
    };
  }

  /**
   * Initialize the component
   */
  init () {
    this.setupElement();
    this.setupEventListeners();
    return this;
  }

  /**
   * Setup the DOM element
   */
  setupElement () {
    if (this.options.id) {
      this.element.id = this.options.id;
    }

    if (this.options.className) {
      this.element.classList.add(...this.options.className.split(' '));
    }

    // Set attributes
    Object.entries(this.options.attributes).forEach(([key, value]) => {
      this.element.setAttribute(key, value);
    });
  }

  /**
   * Setup event listeners
   */
  setupEventListeners () {
    // Override in subclasses
  }

  /**
   * Add event listener with cleanup tracking
   */
  addEventListener (event, handler, options = {}) {
    if (this.isDestroyed) return;

    this.element.addEventListener(event, handler, options);

    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }

    this.eventListeners.get(event).push({ handler, options });
  }

  /**
   * Remove event listener
   */
  removeEventListener (event, handler) {
    this.element.removeEventListener(event, handler);

    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.findIndex(l => l.handler === handler);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Show the component
   */
  show () {
    if (this.element) {
      this.element.style.display = '';
      this.element.classList.remove('hidden');
    }
    this.emit('shown');
  }

  /**
   * Hide the component
   */
  hide () {
    if (this.element) {
      this.element.style.display = 'none';
      this.element.classList.add('hidden');
    }
    this.emit('hidden');
  }

  /**
   * Toggle visibility
   */
  toggle () {
    if (this.isHidden()) {
      this.show();
    } else {
      this.hide();
    }
  }

  /**
   * Check if component is hidden
   */
  isHidden () {
    return this.element.classList.contains('hidden') ||
           (this.element.style.display === 'none');
  }

  /**
   * Add CSS class
   */
  addClass (className) {
    if (this.element) {
      this.element.classList.add(className);
    }
  }

  /**
   * Remove CSS class
   */
  removeClass (className) {
    if (this.element) {
      this.element.classList.remove(className);
    }
  }

  /**
   * Toggle CSS class
   */
  toggleClass (className) {
    if (this.element) {
      this.element.classList.toggle(className);
    }
  }

  /**
   * Set element HTML
   */
  setHTML (html) {
    if (this.element) {
      this.element.innerHTML = html;
    }
  }

  /**
   * Get element HTML
   */
  getHTML () {
    return this.element ? this.element.innerHTML : '';
  }

  /**
   * Emit custom event
   */
  emit (eventName, data = {}) {
    if (this.isDestroyed) return;

    const event = new CustomEvent(eventName, {
      detail: data,
      bubbles: true
    });
    this.element.dispatchEvent(event);
  }

  /**
   * Listen for custom event
   */
  on (eventName, handler) {
    this.addEventListener(eventName, (event) => {
      handler(event.detail, event);
    });
  }

  /**
   * Destroy the component and clean up
   */
  destroy () {
    if (this.isDestroyed) return;

    // Remove all event listeners
    this.eventListeners.forEach((listeners, event) => {
      listeners.forEach(({ handler }) => {
        this.element.removeEventListener(event, handler);
      });
    });
    this.eventListeners.clear();

    // Remove element from DOM
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }

    this.isDestroyed = true;
    this.emit('destroyed');
  }

  /**
   * Get component element
   */
  getElement () {
    return this.element;
  }

  /**
   * Set component options
   */
  setOptions (options) {
    this.options = { ...this.options, ...options };
    return this;
  }

  /**
   * Get component options
   */
  getOptions () {
    return { ...this.options };
  }

  /**
   * Create component from template
   */
  static fromTemplate (template, options = {}) {
    const element = document.createElement('div');
    element.innerHTML = template;
    const componentElement = element.firstElementChild;
    return new this(componentElement, options);
  }

  /**
   * Create component and append to parent
   */
  static createAndAppend (parent, options = {}) {
    const element = document.createElement('div');
    const component = new this(element, options);

    if (typeof parent === 'string') {
      parent = document.querySelector(parent);
    }

    if (parent) {
      parent.appendChild(element);
    }

    return component.init();
  }
}
